<!--
	@component
	Generates an SVG Sankey chart using [d3-sankey](https://github.com/d3/d3-sankey).
 -->
<script>
	import * as Sankey from "d3-sankey";
	import { LayerCake, ScaledSvg, Html } from "layercake";
	import Shapes from "./Shapes.svelte";
	import Labels from "./Labels.svelte";
	import { writable, derived } from "svelte/store";

	export let data;

	/** @type {Function|String} [colorLinks=d => 'rgba(0, 0, 0, .2)'] – A function to return a color for the links. If its value is not a function but the string "gradient", the link color will have a gradient from the source node color to the target node color. */
	export let colorLinks = (d) => "rgba(0, 0, 0, .2)";

	/** @type {Function} [colorNodes=d => '#333'] – A function to return a color for each node. */
	export let colorNodes = (d) => "#333";

	/** @type {Number} [nodeWidth=5] – The width of each node, in pixels, passed to [`sankey.nodeWidth`](https://github.com/d3/d3-sankey#sankey_nodeWidth). */
	export let nodeWidth = 5;

	/** @type {Number} [nodePadding=10] – The padding between nodes, passed to [`sankey.nodePadding`](https://github.com/d3/d3-sankey#sankey_nodePadding). */
	export let nodePadding = 10;

	/** @type {Function} [linkSort=null] – How to sort the links, passed to [`sankey.linkSort`](https://github.com/d3/d3-sankey#sankey_linkSort). */
	export let linkSort = null;

	/** @type {Function} [nodeId=d => d.id] – The ID field accessor, passed to [`sankey.nodeId`](https://github.com/d3/d3-sankey#sankey_nodeId). */
	export let nodeId = (d) => d.id;

	/** @type {Function} [nodeAlign=d3.sankeyLeft] – An alignment function to position the Sankey blocks. See the [d3-sankey documentation](https://github.com/d3/d3-sankey#alignments) for more. */
	export let nodeAlign = Sankey.sankeyLeft;

	/** @type {Object} [padding={left: 88, right: 82}] – Padding for the chart. */
	export let padding;

	// create the sankey generator
	$: sankey = Sankey.sankey()
		.nodeAlign(nodeAlign)
		.nodeWidth(nodeWidth)
		.nodePadding(nodePadding)
		.nodeId(nodeId)
		.size([100, 100])
		.linkSort(linkSort);

	$: sankeyData = sankey(data);

	$: sankeyLink = Sankey.sankeyLinkHorizontal();

	// if the link has an id, use that, otherwise use the index
	const getLinkId = (link) => ("id" in link ? link.id : link.index);

	// store the highlighted links and nodes and handle hovering
	export const linkHighlight = writable([]);
	export const nodeHighlight = writable([]);
	export const highlightExists = derived(
		[linkHighlight, nodeHighlight],
		([$linkHighlight, $nodeHighlight]) => $linkHighlight.length > 0 || $nodeHighlight.length > 0
	);

	const handleLinkHover = (link) => {
		$linkHighlight = [getLinkId(link)];
		$nodeHighlight = [link.source.id, link.target.id];
	};

	const handleNodeHover = (node) => {
		let connections = node.sourceLinks.length == 0 ? node.targetLinks : node.sourceLinks;
		let connectedIds = connections.map((d) => getLinkId(d));

		$linkHighlight = connectedIds;
		$nodeHighlight = [node.id];
	};

	const resetHover = () => {
		$linkHighlight = [];
		$nodeHighlight = [];
	};
</script>

<div class="sankey-container">
	<LayerCake
		data={sankeyData}
		ssr={true}
		percentRange
		custom={{
			nodeWidth,
			linkHighlight,
			nodeHighlight,
			highlightExists,
			getLinkId,
			handleLinkHover,
			handleNodeHover,
			resetHover,
		}}
		{padding}
	>
		<Html>
			<Labels let:node let:type let:highlighted let:link>
				<slot slot="node_label" name="node_label" {node} {type} {highlighted} />
				<slot slot="link_label" name="link_label" {link} {highlighted} />
			</Labels>
		</Html>
		<ScaledSvg>
			<Shapes {colorLinks} {colorNodes} link={sankeyLink}></Shapes>
		</ScaledSvg>
	</LayerCake>
</div>

<style>
	.sankey-container {
		width: 100%;
		height: 500px;
	}

	slot {
		position: relative;
	}
</style>
